import React from 'react';
import { Avatar, Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { IconFile, IconDownload } from '@tabler/icons';
import { formatDistanceToNow, parseJSON, format } from 'date-fns';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  CaseMessage as CaseMessageType,
  CaseMessageAttachment,
} from '../../../types/support.types';

const extensions = [StarterKit, Underline, Link];

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function plainTextToHtml(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

function renderBody(message: CaseMessageType): string {
  if (message.body && typeof message.body === 'object') {
    try {
      return generateHTML(message.body, extensions);
    } catch {
      return plainTextToHtml(message.body_text || '');
    }
  }
  if (typeof message.body === 'string') {
    return plainTextToHtml(message.body);
  }
  return plainTextToHtml(message.body_text || '');
}

function renderBodyPlain(message: CaseMessageType): string {
  return message.body_text || '';
}

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageAttachment(att: CaseMessageAttachment): boolean {
  return !!att.content_type && att.content_type.startsWith('image/');
}

interface CaseMessageProps {
  message: CaseMessageType;
  align: 'left' | 'right' | 'center';
  onDownloadAttachment?: (attachment: CaseMessageAttachment) => void;
  getAttachmentUrl?: (attachment: CaseMessageAttachment) => Promise<string>;
}

const AUTHOR_TYPE_LABELS: Record<string, string> = {
  customer: 'Customer',
  internal: 'Support',
  system: 'System',
};

function getAuthorLabel(message: CaseMessageType): string {
  if (message.author) {
    return message.author.display_name || message.author.email;
  }
  return AUTHOR_TYPE_LABELS[message.author_type] || message.author_type;
}

// Lazy-loading image preview for image attachments
const ImageAttachmentPreview: React.FC<{
  attachment: CaseMessageAttachment;
  getUrl: () => Promise<string>;
  onDownload?: () => void;
}> = ({ attachment, getUrl, onDownload }) => {
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    getUrl()
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [getUrl]);

  if (error) {
    // Fall back to file row on error
    return null;
  }

  return (
    <Box
      onClick={onDownload}
      sx={{
        mt: 0.5,
        cursor: onDownload ? 'pointer' : 'default',
        borderRadius: 1,
        overflow: 'hidden',
        maxWidth: 240,
        bgcolor: 'action.hover',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={attachment.file_name}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: 200,
            objectFit: 'contain',
            borderRadius: 4,
          }}
        />
      ) : (
        <Skeleton variant="rectangular" width={200} height={120} />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
        }}
      >
        <Typography variant="caption" noWrap sx={{ flex: 1 }}>
          {attachment.file_name}
        </Typography>
        {attachment.size_bytes != null && (
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(attachment.size_bytes)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const CaseMessage: React.FC<CaseMessageProps> = ({
  message,
  align,
  onDownloadAttachment,
  getAttachmentUrl,
}) => {
  const authorLabel = getAuthorLabel(message);
  const initial = authorLabel[0].toUpperCase();

  if (align === 'center') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Typography variant="caption" color="text.disabled">
          {renderBodyPlain(message)}
          {' \u00B7 '}
          {formatDistanceToNow(parseJSON(message.created_at), {
            addSuffix: true,
          })}
        </Typography>
      </Box>
    );
  }

  const isRight = align === 'right';
  const html = renderBody(message);
  const attachments = message.attachments || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isRight ? 'row-reverse' : 'row',
        gap: 1,
        mb: 2,
        maxWidth: '80%',
        alignSelf: isRight ? 'flex-end' : 'flex-start',
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.875rem',
          bgcolor: isRight ? 'primary.main' : 'grey.400',
        }}
      >
        {initial}
      </Avatar>
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
            flexDirection: isRight ? 'row-reverse' : 'row',
          }}
        >
          <Typography variant="caption" fontWeight={600}>
            {authorLabel}
          </Typography>
          <Tooltip
            title={format(parseJSON(message.created_at), 'PPpp')}
            placement="top"
          >
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(parseJSON(message.created_at), {
                addSuffix: true,
              })}
            </Typography>
          </Tooltip>
        </Box>
        <Box
          sx={{
            bgcolor: isRight ? 'primary.main' : 'grey.100',
            color: isRight ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
            px: 2,
            py: 1,
            '& p': { m: 0 },
            '& a': {
              color: isRight ? 'primary.contrastText' : 'primary.main',
            },
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {attachments.length > 0 && (
          <Box
            sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            {attachments.map((att) => {
              if (isImageAttachment(att) && getAttachmentUrl) {
                return (
                  <ImageAttachmentPreview
                    key={att.id}
                    attachment={att}
                    getUrl={() => getAttachmentUrl(att)}
                    onDownload={() => onDownloadAttachment?.(att)}
                  />
                );
              }
              return (
                <Box
                  key={att.id}
                  onClick={() => onDownloadAttachment?.(att)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    cursor: onDownloadAttachment ? 'pointer' : 'default',
                    '&:hover': onDownloadAttachment
                      ? { bgcolor: 'action.selected' }
                      : {},
                  }}
                >
                  <IconFile size={14} />
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {att.file_name}
                  </Typography>
                  {att.size_bytes != null && (
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(att.size_bytes)}
                    </Typography>
                  )}
                  {onDownloadAttachment && <IconDownload size={14} />}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CaseMessage;
